// Unified facade for track data.
// - When authenticated: delegates to the API.
// - When unauthenticated: serves a bundled read-only default list plus at most
//   one user-created custom track, persisted in localStorage. Attempting to
//   create a second custom track throws LoginRequiredError so the caller can
//   prompt the user to sign in.

import {
  ApiTrackDto,
  CreateTrackBody,
  UpdateTrackBody,
  trackApiService,
} from './trackApiService';
import * as authService from './authService';
import * as localFileStore from './localFileStore';

const GUEST_TRACK_KEY = 'guestCustomTrack';
const GUEST_TRACK_ID = 'guest-custom';
const DEFAULT_TRACKS_URL = `${process.env.PUBLIC_URL ?? ''}/default-tracks.json`;

export class LoginRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginRequiredError';
  }
}

function isAuthenticated(): boolean {
  return !!authService.getStoredToken();
}

// ── Default tracks (cached after first fetch) ────────────────────────────────

let defaultsCache: ApiTrackDto[] | null = null;

async function loadDefaults(): Promise<ApiTrackDto[]> {
  if (defaultsCache) return defaultsCache;
  const res = await fetch(DEFAULT_TRACKS_URL);
  if (!res.ok) throw new Error(`Failed to load default tracks (${res.status})`);
  const data = (await res.json()) as ApiTrackDto[];
  defaultsCache = data;
  return data;
}

export function isDefaultTrackId(id: string): boolean {
  return id.startsWith('default-');
}

export function isGuestCustomTrackId(id: string): boolean {
  return id === GUEST_TRACK_ID;
}

// ── Guest custom track storage ───────────────────────────────────────────────

function readGuestTrack(): ApiTrackDto | null {
  const raw = localStorage.getItem(GUEST_TRACK_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApiTrackDto;
  } catch {
    return null;
  }
}

function writeGuestTrack(track: ApiTrackDto): void {
  localStorage.setItem(GUEST_TRACK_KEY, JSON.stringify(track));
}

function clearGuestTrack(): void {
  localStorage.removeItem(GUEST_TRACK_KEY);
}

export function hasGuestCustomTrack(): boolean {
  return readGuestTrack() !== null;
}

/**
 * Called after a successful sign-in / registration. If the guest had created a
 * custom track while signed out AND the account has no existing tracks of its
 * own, push the guest track up to the API. The local copy is cleared on
 * success; if the account already has tracks, the guest copy is left intact so
 * the user can still see/manage it the next time they sign out.
 *
 * Returns the created track when migration happened, otherwise null.
 */
export async function migrateGuestTrackOnLogin(): Promise<ApiTrackDto | null> {
  const guest = readGuestTrack();
  if (!guest) {
    // eslint-disable-next-line no-console
    console.info('[migrateGuestTrackOnLogin] no guest track in localStorage; nothing to migrate.');
    return null;
  }
  try {
    const existing = await trackApiService.getAll();
    if (existing.length > 0) {
      // eslint-disable-next-line no-console
      console.info(
        `[migrateGuestTrackOnLogin] account already has ${existing.length} track(s); leaving guest track in place.`,
      );
      return null;
    }
    const created = await trackApiService.create({
      name: guest.name,
      bpm: guest.bpm,
      defaultPitchShift: guest.defaultPitchShift,
      youtubeId: guest.youtubeId ?? null,
      audioFile: guest.audioFile ?? null,
      parts: guest.parts,
      links: guest.links,
    });
    // Re-key any locally-stored MP3 blob from the guest id to the new track id
    // so the migrated track keeps its audio on this device.
    try {
      await localFileStore.renameFile(GUEST_TRACK_ID, created.id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[migrateGuestTrackOnLogin] failed to re-key local MP3 blob:', e);
    }
    clearGuestTrack();
    // eslint-disable-next-line no-console
    console.info(`[migrateGuestTrackOnLogin] migrated guest track to server id ${created.id}.`);
    return created;
  } catch (e) {
    // Leave the guest track in place so it isn't lost if the API call fails.
    // eslint-disable-next-line no-console
    console.warn('[migrateGuestTrackOnLogin] migration failed; guest track preserved:', e);
    return null;
  }
}

// ── Public API (mirrors trackApiService shape) ───────────────────────────────

export const trackStore = {
  async getAll(): Promise<ApiTrackDto[]> {
    if (isAuthenticated()) {
      return trackApiService.getAll();
    }
    const defaults = await loadDefaults();
    const guest = readGuestTrack();
    return guest ? [...defaults, guest] : defaults;
  },

  async getById(id: string): Promise<ApiTrackDto> {
    if (isAuthenticated()) {
      return trackApiService.getById(id);
    }
    if (id === GUEST_TRACK_ID) {
      const guest = readGuestTrack();
      if (!guest) throw new Error('Custom track not found');
      return guest;
    }
    const defaults = await loadDefaults();
    const match = defaults.find((t) => t.id === id);
    if (!match) throw new Error(`Track ${id} not found`);
    return match;
  },

  async create(body: CreateTrackBody): Promise<ApiTrackDto> {
    if (isAuthenticated()) {
      return trackApiService.create(body);
    }
    if (hasGuestCustomTrack()) {
      throw new LoginRequiredError(
        'You can only create one custom track without an account. Sign in or register to create more.',
      );
    }
    const track: ApiTrackDto = {
      id: GUEST_TRACK_ID,
      name: body.name,
      youtubeId: body.youtubeId ?? null,
      audioFile: body.audioFile ?? null,
      bpm: body.bpm,
      defaultPitchShift: body.defaultPitchShift,
      parts: body.parts,
      links: body.links,
    };
    writeGuestTrack(track);
    return track;
  },

  async update(id: string, body: UpdateTrackBody): Promise<ApiTrackDto> {
    if (isAuthenticated()) {
      return trackApiService.update(id, body);
    }
    if (id !== GUEST_TRACK_ID) {
      throw new Error('Default tracks are read-only. Sign in to edit your own tracks.');
    }
    const track: ApiTrackDto = {
      id: GUEST_TRACK_ID,
      name: body.name,
      youtubeId: body.youtubeId ?? null,
      audioFile: body.audioFile ?? null,
      bpm: body.bpm,
      defaultPitchShift: body.defaultPitchShift,
      parts: body.parts,
      links: body.links,
    };
    writeGuestTrack(track);
    return track;
  },

  async delete(id: string): Promise<void> {
    if (isAuthenticated()) {
      return trackApiService.delete(id);
    }
    if (id !== GUEST_TRACK_ID) {
      throw new Error('Default tracks cannot be deleted.');
    }
    clearGuestTrack();
  },
};
